import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Card — surface primitive aligned to the Lopettia design system
 * (Lorenza/Contextus). Three tiers map to bg-card → bg-card-hi →
 * bg-mid; the default tier carries a subtle teal-tinted gradient and
 * a faint border so cards feel lifted off the navy canvas without
 * harsh edges.
 *
 * Tiers:
 *   default  surface-2  → standard card on the page canvas
 *   raised   surface-3  → modals, popovers, side panels (extra shadow)
 *   sunken   surface-sunken → low-emphasis blocks (alt rows, disabled)
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tier?: 'default' | 'raised' | 'sunken';
}

const tierClasses: Record<NonNullable<CardProps['tier']>, string> = {
  default:
    'bg-[linear-gradient(135deg,var(--color-lop-bg-card),var(--color-lop-bg-mid))] border border-[var(--color-lop-border)] text-[var(--color-lop-text-primary)] shadow-[var(--shadow-lop-sm)]',
  raised:
    'bg-[var(--color-lop-bg-card-hi)] border border-[var(--color-lop-border-bright)] text-[var(--color-lop-text-primary)] shadow-[var(--shadow-lop-md)]',
  sunken:
    'bg-[var(--color-lop-bg-mid)] border border-[var(--color-lop-border)] text-[var(--color-lop-text-secondary)]',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { tier = 'default', className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('rounded-[10px] p-6', tierClasses[tier], className)}
      {...props}
    />
  );
});

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex items-center justify-between', className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn(
        'font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--color-lop-text-muted)]',
        className,
      )}
      {...props}
    />
  );
}
